/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {CloseButton} from '../../components/buttons/CloseButton';
import {dlsColor} from '../../theme/theme';
import {HeaderTypography} from '../../typography/HeaderTypography';

export interface BasemapGalleryHeaderContentProps {
  isCurrent: boolean;
  onClose: () => void;
  legacyLabelClassName?: string;
  label?: string;
  // a current-theme header action shown before the close button ...
  action?: ReactNode;
}

const useStyles = makeStyles(() => ({
  title : {
    fontFamily : 'Inter',
    fontStyle  : 'normal',
    fontWeight : 600,
    fontSize   : '16px',
    lineHeight : '20px',
    color      : dlsColor('grey1')
  },
  actions : {
    display    : 'flex',
    alignItems : 'center',
    gap        : 8
  }
}));

/**
 * Renders a basemap gallery panel header's inner content - shared between
 * `BasemapGallery.tsx` (Tracking2's own panel) and
 * `BasemapGalleryTool.tsx` (the legacy/Admin-Geofences panel), so the
 * current-theme title-and-close-button markup and the legacy-theme label
 * have one implementation instead of two independently-maintained copies.
 * The caller still owns the header's own outer container element and its
 * background/height styling, since those differ per caller (e.g. a
 * bottom-sheet breadcrumb variant that never reaches this component).
 *
 * ```typescript
 *
 *   import {BasemapGalleryHeaderContent} from
 *     '@ion/dls/src/maps/layers/BasemapGalleryHeaderContent';
 *
 *   <BasemapGalleryHeaderContent isCurrent={isCurrent}
 *     onClose={toggleBasemapGallery}
 *     legacyLabelClassName={classes.galleryHeader} />
 *
 * ```
 *
 * @param   isCurrent            Renders the current-theme title/close-button
 *                               look when `true`, the legacy-theme label
 *                               otherwise.
 * @param   onClose              Invoked when the current-theme close button
 *                               is clicked.
 * @param   legacyLabelClassName Class name applied to the legacy-theme label,
 *                               since its styling is caller-specific.
 * @param   label                The header's title, the basemap gallery's own
 *                               title by default.
 * @param   action               A current-theme action shown before the close
 *                               button, if any.
 * @returns                      The header's inner content for the given
 *                               theme.
 */
export const BasemapGalleryHeaderContent = ({
  isCurrent,
  onClose,
  legacyLabelClassName,
  label = translate('maps:basemaps.header'),
  action
}: BasemapGalleryHeaderContentProps) => {
  const classes = useStyles();

  return isCurrent ? (
    <>
      <span className={classes.title}>
        {label}
      </span>
      {action ? (
        <div className={classes.actions}>
          {action}
          <CloseButton onClick={onClose} />
        </div>
      ) : (
        <CloseButton onClick={onClose} />
      )}
    </>
  ) : (
    <HeaderTypography variant='h4' className={legacyLabelClassName}>
      {label}
    </HeaderTypography>
  );
};

export default BasemapGalleryHeaderContent;
